import React, { useState, KeyboardEvent, useRef, FocusEvent } from 'react';
import { cx } from '@emotion/css';
import { OutlinedInput, IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useGoogleTranslator } from '@/hooks/useGoogleTranslator';
import CancelIcon from '@mui/icons-material/Cancel';
import styles from './SearchBox.styles';

interface Props {
  autoFocus?: boolean;
  showCancelButton?: boolean;
  value: string;
  placeholder: string;
  isTextBold?: boolean;
  onChange: (text: string) => void;
  onBlur?: () => void;
  onClick?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onResetTextSearch?: () => void;
  endAdornment?: React.ReactNode;
  'aria-describedby'?: string;
}

export const SearchBox: React.FC<Props> = ({
  value,
  placeholder,
  isTextBold = false,
  onChange,
  onBlur,
  onClick,
  onKeyDown,
  endAdornment,
  showCancelButton = false,
  autoFocus = false,
  onResetTextSearch,
  'aria-describedby': ariaDescribedBy,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const placeholderTranslated = useGoogleTranslator(placeholder);

  const onChangeInputSearch = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    onChange(value);
  };

  const resetTextSearch = () => {
    onChange('');
    if (onResetTextSearch) onResetTextSearch();
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onClick) onClick();
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.relatedTarget instanceof Node && containerRef.current?.contains(e.relatedTarget)) {
      return;
    }

    setIsFocused(false);
    if (onBlur) onBlur();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      inputRef.current?.blur();
      setIsFocused(false);
    }
    if (onKeyDown) onKeyDown(e);
  };

  const CancelButton = () => {
    if (showCancelButton && value) {
      return (
        <InputAdornment position="end">
          <IconButton
            onClick={resetTextSearch}
            edge="end"
            className={styles.cancelIcon}
            aria-label="Clear the text search"
          >
            <CancelIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <label htmlFor="search-input" className="visually-hidden">
        {placeholderTranslated}
      </label>
      <OutlinedInput
        id="search-input"
        aria-labelledby="search-input"
        aria-describedby={ariaDescribedBy}
        inputRef={inputRef}
        autoComplete="off"
        autoFocus={autoFocus}
        className={cx(
          styles.searchBar,
          isTextBold && value.length > 0 && !isFocused && styles.textBold
        )}
        value={value}
        onChange={onChangeInputSearch}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholderTranslated}
        data-testid="searchInput"
        startAdornment={
          <InputAdornment position="start" className={styles.iconContainer}>
            <SearchIcon className={styles.iconSearch} sx={{ fontSize: 20 }} />
          </InputAdornment>
        }
        endAdornment={endAdornment || <CancelButton />}
      />
    </div>
  );
};
